import { Statistics, Category, Hashtag } from '@/types';

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3030';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Users API
  async getUsers() {
    return this.request('/api/users');
  }

  async getUser(id: string) {
    return this.request(`/api/users/${id}`);
  }

  async createUser(userData: { email: string; password: string; name?: string }) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async loginUser(credentials: { email: string; password: string }) {
    return this.request('/api/users/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Articles API
  async getArticles(params?: {
    search?: string;
    category?: string;
    limit?: number;
    offset?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    
    const queryString = searchParams.toString();
    const endpoint = `/api/articles${queryString ? `?${queryString}` : ''}`;
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await this.request<{data: any[], total: number}>(endpoint);
    return response.data; // Return just the articles array
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getArticle(id: string): Promise<any> {
    return this.request(`/api/articles/${id}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getArticleByTitle(title: string): Promise<any> {
    return this.request(`/api/articles/search/${encodeURIComponent(title)}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createArticle(articleData: any) {
    return this.request('/api/articles', {
      method: 'POST',
      body: JSON.stringify(articleData),
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateArticle(id: string, articleData: any) {
    return this.request(`/api/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(articleData),
    });
  }

  async deleteArticle(id: string) {
    return this.request(`/api/articles/${id}`, {
      method: 'DELETE',
    });
  }

  // Statistics API
  async getStats(): Promise<Statistics> {
    return this.request<Statistics>('/api/stats');
  }

  // Categories API
  async getCategories(): Promise<Category[]> {
    return this.request<Category[]>('/api/categories');
  }

  async searchCategories(query: string): Promise<Category[]> {
    return this.request<Category[]>(`/api/categories/search?q=${encodeURIComponent(query)}`);
  }

  async createCategory(categoryData: { name: string; description?: string; icon?: string; color?: string }) {
    return this.request('/api/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  // Hashtags API
  async getHashtags(): Promise<Hashtag[]> {
    return this.request<Hashtag[]>('/api/hashtags');
  }

  async searchHashtags(query: string): Promise<Hashtag[]> {
    return this.request<Hashtag[]>(`/api/hashtags/search?q=${encodeURIComponent(query)}`);
  }

  async createHashtag(hashtagData: { name: string }) {
    return this.request('/api/hashtags', {
      method: 'POST',
      body: JSON.stringify(hashtagData),
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
